import { useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Recycle, Leaf, Sparkles, Building2, User, Shield, MessageCircle, ArrowRight, Search, FileText, Download, BookOpen,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, sub, accent = "text-orange-400" }) => (
  <div className="glass-card rounded-2xl p-5 md:p-6 hover:bg-white/[0.12] transition-colors duration-300" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${accent}`}>
      <Icon className="w-6 h-6" strokeWidth={1.6} />
    </div>
    <p className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-semibold">{label}</p>
    <p className="text-3xl md:text-4xl font-display font-extrabold text-white mt-1.5">{value}</p>
    <p className="text-xs text-white/60 mt-1">{sub}</p>
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    nombre_alcaldia: "Palma Plus, Saneamiento Urbano",
    eslogan: "Por una ciudad más limpia",
    whatsapp: null,
  });

  useEffect(() => {
    axios.get(`${API}/configuracion/publica`).then(r => setConfig(c => ({ ...c, ...r.data }))).catch(() => {});
  }, []);

  const handleWhatsApp = () => {
    if (config.whatsapp) {
      const num = String(config.whatsapp).replace(/[^\d]/g, "");
      window.open(`https://wa.me/${num}`, "_blank");
    }
  };

  return (
    <div className="min-h-screen hero-gradient relative overflow-hidden" data-testid="landing-page">
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }} />

      {/* TOP BAR */}
      <header className="relative z-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/icon_palmaplus.png" alt="Palma Plus" className="w-14 h-14 rounded-xl shadow-lg shadow-black/20 object-contain bg-white p-1" style={{ boxShadow: "0 0 0 3px rgba(234,88,12,0.35)" }} data-testid="landing-logo" />
            <div className="leading-tight">
              <div className="inline-block px-2 py-0.5 rounded-md bg-orange-500/20 border border-orange-400/30 text-[9px] font-bold uppercase tracking-[0.16em] text-orange-300 mb-0.5">Oficina Virtual</div>
              <p className="font-display font-extrabold text-white text-base md:text-lg leading-tight">{config.nombre_alcaldia}</p>
              <p className="text-[11px] text-white/60 italic">{config.eslogan}</p>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-orange-400 font-bold mr-3">Declara y paga en online</span>
            <button onClick={() => navigate("/login/contribuyente")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-green-900 hover:bg-orange-50 font-semibold text-sm shadow-md transition" data-testid="topbar-contribuyente-btn">
              <User className="w-4 h-4" /> Contribuyente
            </button>
            <button onClick={() => navigate("/login/funcionario")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm shadow-md transition" data-testid="topbar-funcionario-btn">
              <Shield className="w-4 h-4" /> Funcionario
            </button>
            {config.whatsapp && (
              <button onClick={handleWhatsApp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500 hover:bg-green-600 text-white font-semibold text-sm shadow-md transition" data-testid="topbar-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
            )}
          </div>
        </div>
      </header>

      {/* SECONDARY NAV (links + search) */}
      <div className="relative z-10 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          <nav className="flex items-center gap-5 md:gap-7 text-sm font-semibold text-green-900">
            <a className="hover:text-orange-600 transition" href="#ordenanzas">Ordenanzas</a>
            <a className="hover:text-orange-600 transition" href="#manuales">Manuales</a>
            <a className="hover:text-orange-600 transition" href="#tutoriales">Tutoriales</a>
            <a className="hover:text-orange-600 transition" href="#cuentas">Cuentas Bancarias</a>
          </nav>
          <div className="flex items-center gap-2 bg-green-50 rounded-full px-4 py-2 w-full md:w-80 border border-green-100">
            <Search className="w-4 h-4 text-green-700" />
            <input data-testid="landing-search-input" type="text" placeholder="Buscar en el sitio…" className="bg-transparent border-none outline-none text-sm flex-1 text-green-900 placeholder:text-green-700/40" />
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur text-xs font-semibold mb-6">
              <Sparkles className="w-3.5 h-3.5 text-orange-300" />
              Portal del Contribuyente
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mb-6 tracking-tight">
              Por una ciudad <span className="text-orange-400">más limpia.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-xl leading-relaxed mb-8">
              Pague su servicio de aseo urbano municipal de forma rápida, segura y desde donde esté. Cumplir es construir comunidad.
            </p>
            <p className="text-orange-400 font-display font-bold text-sm tracking-wider uppercase mb-6">Declara y paga en online.</p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => navigate("/login/contribuyente")} className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-green-900 hover:bg-orange-50 font-semibold shadow-xl shadow-black/20 transition" data-testid="hero-soy-contribuyente-btn">
                <User className="w-5 h-5" /> Soy Contribuyente
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
              </button>
              <button onClick={() => navigate("/login/funcionario")} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-xl shadow-orange-900/30 transition" data-testid="hero-soy-funcionario-btn">
                <Shield className="w-5 h-5" /> Soy Funcionario
              </button>
              {config.whatsapp && (
                <button onClick={handleWhatsApp} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-green-500 hover:bg-green-600 text-white font-semibold shadow-xl shadow-green-900/30 transition" data-testid="hero-whatsapp-btn">
                  <MessageCircle className="w-5 h-5" /> WhatsApp
                </button>
              )}
            </div>

            <p className="text-base sm:text-lg text-white/85 mt-8 max-w-xl leading-relaxed font-medium">
              ¿Aún no tienes cuenta y tienes Licencia de Actividades Económicas?{" "}
              <button onClick={() => navigate("/registro")} className="text-orange-300 hover:text-orange-200 font-bold underline underline-offset-4 decoration-orange-400/60 hover:decoration-orange-300" data-testid="hero-register-link">
                Regístrate como contribuyente aquí
              </button>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 md:gap-5">
            <StatCard icon={Recycle} label="Aseo Urbano" value="100%" sub="Cobertura municipal" accent="text-orange-400" />
            <StatCard icon={Leaf} label="Cultura Tributaria" value="+12K" sub="Contribuyentes activos" accent="text-green-400" />
            <StatCard icon={Sparkles} label="Trámites" value="24/7" sub="Disponibles en línea" accent="text-orange-400" />
            <StatCard icon={Building2} label="Pagos" value="Seguros" sub="Bancos autorizados" accent="text-green-400" />
          </div>
        </div>
      </section>

      {/* ORDENANZAS */}
      <section id="ordenanzas" className="relative z-10 mt-4 pb-8" data-testid="section-ordenanzas">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="bg-white/95 backdrop-blur rounded-3xl border border-green-100 shadow-2xl shadow-black/30 overflow-hidden">
            {/* Encabezado */}
            <div className="bg-gradient-to-r from-green-900 to-green-800 text-white px-6 py-5 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-orange-300 mb-1">Marco Legal</p>
                <h2 className="font-display font-extrabold text-2xl md:text-3xl">Ordenanzas Municipales</h2>
                <p className="text-white/70 text-sm mt-1">Documentos oficiales que regulan el servicio de aseo urbano.</p>
              </div>
              <div className="hidden md:flex w-14 h-14 rounded-2xl bg-orange-500/20 border-2 border-orange-400/40 items-center justify-center">
                <FileText className="w-7 h-7 text-orange-300" />
              </div>
            </div>

            {/* Cuerpo */}
            <div className="p-6 md:p-8">
              <a
                href="/ordenanzas/ordenanza-residuos-solidos-jose-felix-ribas.pdf"
                download="Ordenanza-Residuos-Solidos-Jose-Felix-Ribas.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 md:p-6 rounded-2xl border-2 border-green-100 hover:border-orange-300 hover:bg-orange-50/40 transition"
                data-testid="ordenanza-residuos-solidos-download"
              >
                {/* Ícono grande PDF */}
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-green-800 to-green-900 flex items-center justify-center shrink-0 shadow-lg">
                  <FileText className="w-8 h-8 text-orange-300" strokeWidth={1.7} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-orange-600 mb-1">Gaceta Municipal · PDF Oficial</p>
                  <h3 className="font-display font-extrabold text-lg md:text-xl text-green-900 leading-tight group-hover:text-orange-700 transition">
                    Ordenanza de Gestión Integral de los Residuos y Desechos Sólidos del Municipio José Félix Ribas
                  </h3>
                  <p className="text-sm text-green-800/70 mt-1">
                    Documento oficial vigente. Descargue el PDF para consulta y referencia legal.
                  </p>
                </div>

                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-orange-500 group-hover:bg-orange-600 text-white font-semibold text-sm shadow-md shadow-orange-900/20 transition shrink-0">
                  <Download className="w-4 h-4" /> Descargar PDF
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* MANUALES */}
      <section id="manuales" className="relative z-10 mt-4 pb-8" data-testid="section-manuales">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="bg-white/95 backdrop-blur rounded-3xl border border-green-100 shadow-2xl shadow-black/30 overflow-hidden">
            {/* Encabezado */}
            <div className="bg-gradient-to-r from-green-900 to-green-800 text-white px-6 py-5 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-orange-300 mb-1">Guías Ilustradas</p>
                <h2 className="font-display font-extrabold text-2xl md:text-3xl">Manuales</h2>
                <p className="text-white/70 text-sm mt-1">Aprende a usar el Portal del Contribuyente paso a paso.</p>
              </div>
              <div className="hidden md:flex w-14 h-14 rounded-2xl bg-orange-500/20 border-2 border-orange-400/40 items-center justify-center">
                <BookOpen className="w-7 h-7 text-orange-300" />
              </div>
            </div>

            {/* Cuerpo */}
            <div className="p-6 md:p-8">
              <a
                href={`${API}/manuales/publico/contribuyente/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 md:p-6 rounded-2xl border-2 border-green-100 hover:border-orange-300 hover:bg-orange-50/40 transition"
                data-testid="manual-contribuyente-download"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-green-800 to-green-900 flex items-center justify-center shrink-0 shadow-lg">
                  <BookOpen className="w-8 h-8 text-orange-300" strokeWidth={1.7} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-orange-600 mb-1">Manual de Usuario · PDF · Guía ilustrada</p>
                  <h3 className="font-display font-extrabold text-lg md:text-xl text-green-900 leading-tight group-hover:text-orange-700 transition">
                    Manual del Contribuyente
                  </h3>
                  <p className="text-sm text-green-800/70 mt-1">
                    Cómo registrarte, consultar tu deuda, reportar pagos, descargar planillas y solvencias desde el portal.
                  </p>
                </div>

                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-orange-500 group-hover:bg-orange-600 text-white font-semibold text-sm shadow-md shadow-orange-900/20 transition shrink-0">
                  <Download className="w-4 h-4" /> Descargar PDF
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FORMAS DE PAGO */}
      <section className="relative z-10 mt-4 pb-8" data-testid="section-formas-pago">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="bg-white/95 backdrop-blur rounded-3xl border border-green-100 shadow-2xl shadow-black/30 overflow-hidden">
            {/* Encabezado */}
            <div className="bg-gradient-to-r from-green-900 to-green-800 text-white px-6 py-5 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-orange-300 mb-1">Formas de Pago</p>
                <h2 className="font-display font-extrabold text-2xl md:text-3xl">Al Servicio de Aseo Urbano</h2>
                <p className="text-white/70 text-sm mt-1">Opciones cómodas, seguras y accesibles para mantener tu cuenta al día.</p>
              </div>
              <div className="hidden md:block w-14 h-14 rounded-2xl bg-orange-500/20 border-2 border-orange-400/40 flex items-center justify-center">
                <span className="text-2xl">💳</span>
              </div>
            </div>

            {/* ¿Dónde? */}
            <div className="px-6 py-6 border-b border-green-100">
              <h3 className="text-green-900 font-bold text-center mb-4 text-sm tracking-widest uppercase">¿Dónde puedes realizar tu pago?</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { t: "Nuestras Oficinas", d: "Visítanos en Av. Soco, Centro Comercial Palma Center, local PB-33, La Victoria, Edo Aragua." },
                  { t: "Oficina de Hacienda", d: "En la taquilla identificada como Palma Plus dentro de la Oficina de Hacienda." },
                  { t: "Agentes de Cobro", d: "Nuestros agentes visitarán tu oficina o negocio para facilitar tu pago." },
                ].map((x, i) => (
                  <div key={i} className="bg-green-50 rounded-2xl p-5 border border-green-100 text-center">
                    <p className="font-display font-bold text-green-900 mb-2">{x.t}</p>
                    <p className="text-xs text-green-800/70 leading-relaxed">{x.d}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Cuentas bancarias */}
            <div className="px-6 py-6 bg-green-50/40">
              <h3 className="text-green-900 font-bold text-center mb-4 text-sm tracking-widest uppercase">Cuentas Bancarias Autorizadas</h3>
              <div className="grid md:grid-cols-3 gap-4" data-testid="cuentas-bancarias-publicas">
                {/* Oficina */}
                <div className="bg-white rounded-2xl p-5 border border-green-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b border-green-100">
                    <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xs">📍</span>
                    <p className="text-[10px] font-bold text-green-900 uppercase tracking-wider">Nuestra Oficina</p>
                  </div>
                  <p className="font-display font-bold text-green-900 leading-snug">Palma Plus · Palma Center</p>
                  <p className="text-xs text-slate-600 mt-2 leading-relaxed">Av. Soco, Centro Comercial Palma Center, local PB-33. La Victoria, Edo Aragua.</p>
                  <p className="text-xs text-slate-600 mt-2">🕒 <strong>Lunes a Viernes</strong><br />8:00 a.m. a 4:00 p.m.</p>
                </div>
                {/* Pago Móvil */}
                <div className="bg-white rounded-2xl p-5 border-2 border-orange-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b border-orange-100">
                    <span className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 text-xs">📱</span>
                    <p className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">Pago Móvil</p>
                  </div>
                  <p className="font-mono font-extrabold text-2xl text-green-900" data-testid="pago-movil-telefono">0414-2404616</p>
                  <p className="text-sm text-slate-700 mt-3"><strong>A nombre:</strong> Palma Plus, Saneamiento Urbano</p>
                  <p className="text-sm text-slate-700"><strong>RIF:</strong> <span className="font-mono">J-50836696-4</span></p>
                  <p className="text-sm text-slate-700"><strong>Banco:</strong> 0151 – BFC (Banco Fondo Común)</p>
                </div>
                {/* Transferencia */}
                <div className="bg-white rounded-2xl p-5 border-2 border-orange-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b border-orange-100">
                    <span className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 text-xs">🏛️</span>
                    <p className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">Transferencia Bancaria</p>
                  </div>
                  <p className="font-mono font-extrabold text-lg text-green-900 tracking-wide" data-testid="transferencia-cuenta">0151-0115-61-1004789993</p>
                  <p className="text-sm text-slate-700 mt-3"><strong>A nombre:</strong> Palma Plus, Saneamiento Urbano</p>
                  <p className="text-sm text-slate-700"><strong>Banco Fondo Común (BFC)</strong></p>
                  <p className="text-sm text-slate-700"><strong>RIF:</strong> <span className="font-mono">J-50836696-4</span></p>
                </div>
              </div>
            </div>

            {/* Punto de Venta + call to action */}
            <div className="grid md:grid-cols-2 border-t border-green-100">
              <div className="px-6 py-5 border-r border-green-100">
                <p className="font-display font-bold text-green-900 mb-1">💳 Punto de Venta (POS)</p>
                <p className="text-xs text-green-800/70 leading-relaxed">Disponible en nuestras oficinas y en la Oficina de Hacienda. Aceptamos tarjetas de débito/crédito Visa, Mastercard y Maestro.</p>
              </div>
              <div className="px-6 py-5 bg-orange-500 text-white">
                <p className="font-display font-extrabold text-lg mb-1">🌱 PAGA A TIEMPO</p>
                <p className="text-sm font-semibold">Sumamos limpieza, sumamos bienestar para todos.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 border-t border-white/10 mt-10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 text-center md:text-left text-xs text-white/55 flex flex-col md:flex-row justify-between items-center gap-2">
          <p>© {new Date().getFullYear()} {config.nombre_alcaldia}. Todos los derechos reservados.</p>
          <div className="flex items-center gap-4">
            <Link to="/terminos" className="text-white/70 hover:text-white transition" data-testid="footer-terminos-link">
              Términos y Condiciones
            </Link>
            <span className="text-white/30">·</span>
            <p>SISAUR · Sistema de Saneamiento Urbano</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
